/**
 * BotOutputFormatsTab
 * Per-agent toggles for which output files the agent may generate during a
 * conversation. Styled like Claude's connectors/plugins page: each format is a
 * card with its brand icon, name + description, and a toggle switch.
 * Stored in handler_config.output_formats as {pptx, docx, xlsx, gforms, markdown}.
 *
 * The Agent of Agents settings dialog renders this same component for its own
 * Files tab, over a narrower format list — one component so the two surfaces
 * cannot drift into looking like two different products.
 */

import React from 'react';
import { Switch } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import type { OutputFormat } from '@/types/models';
import { OutputFormatIcon } from './outputFormatIcons';
import './BotOutputFormatsTab.css';

interface Props {
  value: Partial<Record<OutputFormat, boolean>>;
  onChange: (next: Partial<Record<OutputFormat, boolean>>) => void;
  disabled?: boolean;
  /** Which formats to offer. Defaults to everything a regular agent may produce. */
  formats?: OutputFormat[];
  /** Floor on how many may be enabled at once — the last one on locks. */
  minEnabled?: number;
  title?: string;
  description?: string;
}

// NOTE: 'html' (HTML→PDF presentation) is intentionally NOT offered to users.
// The renderer still exists behind the scenes (for any previously-created html
// artifacts), but it can no longer be enabled on an agent.
const DEFAULT_FORMATS: OutputFormat[] = ['pptx', 'docx', 'pdf', 'xlsx', 'numbers', 'gforms', 'markdown'];

const BotOutputFormatsTab: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  formats,
  minEnabled = 0,
  title,
  description,
}) => {
  const { t } = useLanguage();
  const list = formats ?? DEFAULT_FORMATS;
  const enabledCount = list.filter((f) => value?.[f]).length;
  const toggle = (fmt: OutputFormat) => onChange({ ...value, [fmt]: !value?.[fmt] });

  return (
    <div className="modal-tab-content output-formats-tab">
      <div className="output-formats-head">
        <div>
          <h3 className="output-formats-head__title">
            {title ?? t('bots.outputFormats.title')}
          </h3>
          <p className="output-formats-head__desc">
            {description ?? t('bots.outputFormats.description')}
          </p>
        </div>
        <span className="output-formats-head__count">
          {t('bots.outputFormats.enabledCount', { count: enabledCount })}
        </span>
      </div>

      <div className="output-formats-list">
        {list.map((fmt) => {
          const on = !!value?.[fmt];
          // Turning off the last one leaves a capability with no format — a
          // tool that could only fail. Lock it here rather than letting Save
          // be the thing that explains it.
          const locked = on && enabledCount <= minEnabled;
          const cardDisabled = disabled || locked;
          return (
            <div
              key={fmt}
              className={`output-format-card ${on ? 'is-on' : ''} ${cardDisabled ? 'is-disabled' : ''}`}
              role="button"
              tabIndex={cardDisabled ? -1 : 0}
              onClick={() => !cardDisabled && toggle(fmt)}
              onKeyDown={(e) => {
                if (!cardDisabled && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  toggle(fmt);
                }
              }}
            >
              <span className="output-format-card__icon" aria-hidden="true">
                <OutputFormatIcon format={fmt} size={26} />
              </span>
              <span className="output-format-card__text">
                <span className="output-format-card__name">{t(`bots.outputFormats.${fmt}`)}</span>
                <span className="output-format-card__desc">
                  {t(`bots.outputFormats.${fmt}Desc`)}
                </span>
              </span>
              <span className={`output-format-card__status ${on ? 'is-on' : ''}`}>
                {on ? t('bots.outputFormats.enabled') : t('bots.outputFormats.disabled')}
              </span>
              <Switch
                className="output-format-card__switch"
                checked={on}
                disabled={cardDisabled}
                onChange={() => toggle(fmt)}
                onClick={(_, e) => e.stopPropagation()}
                data-testid={`output-format-${fmt}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BotOutputFormatsTab;
